import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = useMemo(() => comics.find((c) => c.slug === slug), [slug]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 免费韩国漫画在线阅读 - 韩漫网`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `${comic.title}，${comic.author}作品。${comic.description.substring(0, 100)}... 韩漫网免费提供高清韩国漫画在线阅读。`);
      }
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-book-open-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">该漫画不存在或已被移除</p>
            <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              浏览其他漫画
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩漫网`,
            author: { '@type': 'Person', name: comic.author },
            description: comic.description,
            image: comic.coverImage,
            datePublished: '2026-01-15',
            dateModified: comic.updatedAt,
            url: `http://yxhaomai.com/comic/${comic.slug}`,
            publisher: { '@type': 'Organization', name: '韩漫网' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://yxhaomai.com/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: `http://yxhaomai.com/category/${comic.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `http://yxhaomai.com/comic/${comic.slug}` },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{comic.category}</Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">{comic.title}</span>
              </nav>
            </div>
          </div>

          <section className="py-8 md:py-12 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="lg:w-80 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 免费韩国漫画`}
                      title={`${comic.title} - 免费韩国漫画在线阅读-韩漫网`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <button className="w-full mt-5 px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm">
                    开始阅读
                  </button>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-foreground-500 text-sm mb-4">
                    作者：{comic.author}
                  </p>

                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className={`px-2.5 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-0.5 rounded-md bg-background-200 text-foreground-600 text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-5 mb-6 text-sm text-foreground-600 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500"></i>
                      </div>
                      <span className="font-semibold text-foreground-900">{comic.rating}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-eye-line text-foreground-400"></i>
                      </div>
                      <span>{(comic.views / 10000).toFixed(0)}万次阅读</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-file-list-3-line text-foreground-400"></i>
                      </div>
                      <span>{comic.chapters.length}话</span>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-3">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div>
                    <h2 className="text-sm font-semibold text-foreground-900 mb-4">章节列表</h2>
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                      {[...comic.chapters].reverse().map((chapter) => (
                        <div
                          key={chapter.id}
                          className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                        >
                          <span className="text-sm font-medium text-foreground-800">
                            {chapter.title}
                          </span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">
                            {chapter.updatedAt}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {comics
                  .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
                  .slice(0, 6)
                  .map((c) => (
                    <Link key={c.id} to={`/comic/${c.slug}`} className="group block cursor-pointer">
                      <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2">
                        <img
                          src={c.coverImage}
                          alt={c.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {c.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{c.author}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}